import { AfterViewInit, Component, ViewChild, ViewEncapsulation, OnInit } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Corpus } from '../../../models/corpus';
import { Custodian } from '../../../models/custodian';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { Endpoint, EntityType, HelpIndex, MessageType, ModalTargetType, SaveToRemoveFromOperationType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';
import { CreateEditEntityModalComponent } from '../create-edit-entity-modal/create-edit-entity-modal.component';
import { EditBucketModalComponent, EditMode } from '../edit-bucket/edit-bucket-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../models/permission';
import { SearchResult } from '../../../models/search-result';
import { ViewType } from '../../../models/view';
import { SavedSearch } from '../../../models/saved-searches';
import { Task } from '../../../models/task';
import { MediaID } from '../../../models/mediaid';
import { Batch } from '../../../models/batch';

export interface AddToRemoveFromModalData {
    operationType: SaveToRemoveFromOperationType;
    targetType: ModalTargetType;
    targets: Array<any>;
}

@Component({
    standalone: false,
    selector: 'res-add-to-remove-from-modal',
    templateUrl: './add-to-remove-from-modal.component.html',
    styleUrls: ['./add-to-remove-from-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AddToRemoveFromModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;

    operationType: SaveToRemoveFromOperationType;
    targetType: ModalTargetType;

    targets = [];
    includeFamilies = true;
    modalTitle = '';
    modalAutomationId = 'add-to-remove-from-location';

    isBusy = false;
    isBusyMessage = '';
    modalClass = 'add-to-remove-from';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    modalHelpIndex: HelpIndex;
    resizeHorizontal: boolean;
    resizeVertical: boolean;
    maximizable: boolean;
    inputAutoFocused = true;

    custodiansNode: GenericTreeNode = new GenericTreeNode({
        name: 'Custodians',
        automationId: this.modalAutomationId + '-custodians',
        isExpanded: false,
        isExpandable: true,
        isExpandedOnSelect: true,
        permissions: [{ permissionType: ObjectActionListType.ECA_CUSTODIAN },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        isSelectable: false
    });
    mediaidsNode: GenericTreeNode = new GenericTreeNode({
        name: 'MediaIDs',
        automationId: this.modalAutomationId + '-mediaids',
        isExpanded: false,
        isExpandable: true,
        isExpandedOnSelect: true,
        permissions: [{ permissionType: ObjectActionListType.ECA_MEDIAID },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        isSelectable: false
    });
    batchesNode: GenericTreeNode = new GenericTreeNode({
        name: 'Batches',
        automationId: this.modalAutomationId + '-batches',
        isExpanded: false,
        isExpandable: true,
        isExpandedOnSelect: true,
        permissions: [{ permissionType: ObjectActionListType.ECA_BATCH },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        isSelectable: false
    });
    foldersNode: GenericTreeNode = new GenericTreeNode({
        name: 'Folders',
        automationId: this.modalAutomationId + '-folders',
        isExpanded: false,
        isExpandable: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        permissions: [{ permissionType: ObjectActionListType.ECA_FOLDER },
        { permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] }],
    });
    nodes = new Array<GenericTreeNode>();
    filteredNodes = new Array<GenericTreeNode>();
    selectedNode: GenericTreeNode;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        // ngOnInit is added to toggle between maximizable or non-maximizable window
        const addToRemoveFromModalData = this.modalData as AddToRemoveFromModalData;
        this.targetType = addToRemoveFromModalData.targetType;
        if (this.targetType === ModalTargetType.selection) {
            this.resizeHorizontal = false;
            this.resizeVertical = false;
            this.maximizable = false;
        } else {
            this.resizeHorizontal = true;
            this.resizeVertical = true;
            this.maximizable = true;
        }
    }

    ngAfterViewInit() {
        const addToRemoveFromModalData = this.modalData as AddToRemoveFromModalData;
        const currentTarget = this.modalData.targets && this.modalData.targets.length > 0 ? this.modalData.targets[0] : null;
        const nodeParent = this.modalData.nodeParent;
        this.operationType = addToRemoveFromModalData.operationType;
        this.targets = addToRemoveFromModalData.targets;

        // hide this.folderNode is the targetType is selection
        this.foldersNode.customClasses = this.targetType === ModalTargetType.selection ? 'selection-modal-parent-node' : '';

        this.modalTitle = this.targetType === ModalTargetType.selection ? this.modalTitle = 'Select Folder' :
            this.operationType === SaveToRemoveFromOperationType.add ? 'Add to ...' : 'Remove from ...';
        this.modalHelpIndex = this.targetType === ModalTargetType.selection ? HelpIndex.myedisc_edit_run_open_workflow_dlg : HelpIndex.myedisc_save_documents_to_dlg;
        // creating project data menu
        this.nodes.push(new GenericTreeNode({
            name: 'Project Data',
            automationId: this.modalAutomationId + '-project-data',
            entityType: EntityType.projectData,
            isExpanded: true,
            isExpandable: true,
            isExpandedOnSelect: true,
            customClasses: this.targetType === ModalTargetType.selection ? 'selection-modal-header' : '',
            isSelectable: currentTarget instanceof Corpus || (nodeParent instanceof Corpus) ||
                (nodeParent instanceof SearchResult && (nodeParent.viewType === ViewType.CORPUS_RESULT || nodeParent.viewType === ViewType.CORPUS_SET_RESULT)) ||
                ((currentTarget instanceof SearchResult || currentTarget instanceof Task) && (currentTarget.viewType === ViewType.CORPUS_SET_RESULT || currentTarget.viewType === ViewType.CORPUS_RESULT)),
            children: this.targetType === ModalTargetType.selection ? [this.foldersNode] : [this.custodiansNode, this.mediaidsNode, this.batchesNode, this.foldersNode],
            data: this.commonService.projectData
        }));
        this.tree.setData(this.nodes);
        this.getData();
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            switch (node.name) {
                case 'New Custodian ...':
                    this.newCustodian();
                    break;
                case 'New Folder ...':
                    this.newFolder();
                    break;
                case 'New MediaID ...':
                    this.newMediaID();
                    break;
                case 'New Batch ...':
                    this.newBatch();
                    break;
                default:
                    this.selectedNode = node;
                    break;
            }
        }
    }

    getData(newNode?: any) {
        // custodian menu
        if (Permissions.checkUserPermission(ObjectActionListType.ECA_CUSTODIAN) && this.targetType !== ModalTargetType.selection) {
            this.custodiansNode.isLoading = true;
            this.restService.post(Endpoint.ecaManager_listCustodians, {
                descending: true,
                projectHandle: this.commonService.currentProject.handle
            }).then(response => {
                if (Permissions.checkUserPermission(ObjectActionListType.ECA_CUSTODIAN, ActionType.CREATE)) {
                    this.tree.setNodeData(this.custodiansNode, this.operationType === SaveToRemoveFromOperationType.add ? [new GenericTreeNode({
                        name: 'New Custodian ...',
                        icon: 'fa-plus',
                        iconColor: 'color-green-04',
                        automationId: this.modalAutomationId + '-new-custodian-button',
                        hasExpanderPadding: true,
                        isButton: true,
                        isSelectable: false
                    })] : []);
                }
                this.custodiansNode.isLoading = false;
                if (response.custodians && response.custodians.length > 0) {
                    response.custodians.forEach(el => {
                        if (el.name === 'Unassigned') {
                            return;
                        }
                        const node = new GenericTreeNode({
                            name: el.name,
                            customNumber: el.documentCount,
                            icon: 'fa-user-circle-o',
                            iconColor: 'color-blue-01',
                            entityType: EntityType.custodian,
                            hasExpanderPadding: true,
                            data: new Custodian(el),
                            automationId: el.name.replace(/ /g, '-') + '-custodian-child-node'
                        });

                        this.custodiansNode.children.push(node);
                    });
                }
                this.tree.treeDataUpdated();
                if (newNode && newNode.custodian) {
                    const selNode = this.tree.getNodeByHandle(newNode.custodian.handle, this.custodiansNode);
                    if (selNode) {
                        this.tree.selectNode(selNode);
                    }
                }
            }, error => {
                this.custodiansNode.isLoading = false;
            });
        }

        if (Permissions.checkUserPermission(ObjectActionListType.ECA_MEDIAID) && this.targetType !== ModalTargetType.selection) {
            // mediaid menu
            this.mediaidsNode.isLoading = true;
            this.restService.post(Endpoint.ecaManager_listMediaids, {
                descending: true,
                projectHandle: this.commonService.currentProject.handle,
                includeUnassignedMetadataView: true
            }).then(response => {
                if (Permissions.checkUserPermission(ObjectActionListType.ECA_MEDIAID, ActionType.CREATE)) {
                    this.tree.setNodeData(this.mediaidsNode, this.operationType === SaveToRemoveFromOperationType.add ? [new GenericTreeNode({
                        name: 'New MediaID ...',
                        icon: 'fa-plus',
                        iconColor: 'color-green-04',
                        automationId: this.modalAutomationId + '-new-mediaid-button',
                        hasExpanderPadding: true,
                        isButton: true,
                        isSelectable: false
                    })] : []);
                }
                this.mediaidsNode.isLoading = false;
                if (response.mediaids && response.mediaids.length > 0) {
                    response.mediaids.forEach(el => {
                        if (el.name === 'Unassigned') {
                            return;
                        }
                        const node = new GenericTreeNode({
                            name: el.name,
                            customNumber: el.documentCount,
                            icon: 'fa-laptop',
                            iconColor: 'color-blue-01',
                            entityType: EntityType.mediaID,
                            hasExpanderPadding: true,
                            data: new MediaID(el),
                            automationId: el.name.replace(/ /g, '-') + '-mediaid-child-node'
                        });

                        this.mediaidsNode.children.push(node);
                    });
                }
                this.tree.treeDataUpdated();
                if (newNode?.mediaid) {
                    const selNode = this.tree.getNodeByHandle(newNode.mediaid.handle, this.mediaidsNode);
                    if (selNode) {
                        this.tree.selectNode(selNode);
                    }
                }
            }, error => {
                this.mediaidsNode.isLoading = false;
            });
        }

        if (Permissions.checkUserPermission(ObjectActionListType.ECA_BATCH) && this.targetType !== ModalTargetType.selection) {
            // batch menu
            this.batchesNode.isLoading = true;
            this.restService.post(Endpoint.ecaManager_listBatches, {
                descending: true,
                projectHandle: this.commonService.currentProject.handle,
                includeUnassignedMetadataView: true
            }).then(response => {
                if (Permissions.checkUserPermission(ObjectActionListType.ECA_BATCH, ActionType.CREATE)) {
                    this.tree.setNodeData(this.batchesNode, this.operationType === SaveToRemoveFromOperationType.add ? [new GenericTreeNode({
                        name: 'New Batch ...',
                        icon: 'fa-plus',
                        iconColor: 'color-green-04',
                        automationId: this.modalAutomationId + '-new-batch-button',
                        hasExpanderPadding: true,
                        isButton: true,
                        isSelectable: false
                    })] : []);
                }
                this.batchesNode.isLoading = false;
                if (response.batches && response.batches.length > 0) {
                    response.batches.forEach(el => {
                        if (el.name === 'Unassigned') {
                            return;
                        }
                        const node = new GenericTreeNode({
                            name: el.name,
                            customNumber: el.documentCount,
                            icon: 'fa-archive',
                            iconColor: 'color-blue-01',
                            entityType: EntityType.batch,
                            hasExpanderPadding: true,
                            data: new Batch(el),
                            automationId: el.name.replace(/ /g, '-') + '-batch-child-node'
                        });

                        this.batchesNode.children.push(node);
                    });
                }
                this.tree.treeDataUpdated();
                if (newNode?.batch) {
                    const selNode = this.tree.getNodeByHandle(newNode.batch.handle, this.batchesNode);
                    if (selNode) {
                        this.tree.selectNode(selNode);
                    }
                }
            }, error => {
                this.batchesNode.isLoading = false;
            });
        }

        if (Permissions.checkUserPermission(ObjectActionListType.ECA_FOLDER)) {
            // dynamic folders menu
            this.foldersNode.isLoading = true;
            this.restService.post(Endpoint.projectManager_listDynamicFolders, {
                projectHandle: this.commonService.currentProject.handle
            }).then(response => {
                if (Permissions.checkUserPermission(ObjectActionListType.ECA_FOLDER, ActionType.CREATE)) {
                    if (this.operationType === SaveToRemoveFromOperationType.add) {
                        this.tree.setNodeData(this.foldersNode, this.operationType === SaveToRemoveFromOperationType.add ? [new GenericTreeNode({
                            name: 'New Folder ...',
                            icon: 'fa-plus',
                            iconColor: 'color-green-04',
                            automationId: this.modalAutomationId + '-new-folder-button',
                            hasExpanderPadding: this.targetType === ModalTargetType.selection ? false : true,
                            isButton: true,
                            isSelectable: false,
                            childPadding: this.targetType === ModalTargetType.selection ? 4 : 15
                        })] : []);
                    }
                }
                this.foldersNode.isLoading = false;
                if (response.dynamicFolders && response.dynamicFolders.length > 0) {
                    response.dynamicFolders.forEach(el => {
                        const node = new GenericTreeNode({
                            name: el.name,
                            customNumber: el.view ? el.view.documentCount : 0,
                            icon: 'fa-folder',
                            iconColor: el.color,
                            entityType: EntityType.folder,
                            hasExpanderPadding: this.targetType === ModalTargetType.selection ? false : true,
                            data: new DynamicFolder(el),
                            automationId: el.name.replace(/ /g, '-') + '-folder-child-node',
                            childPadding: this.targetType === ModalTargetType.selection ? 4 : 15
                        });
                        this.foldersNode.children.push(node);
                    });
                }
                this.tree.treeDataUpdated();
                if (newNode && newNode.dynamicFolder) {
                    const selNode = this.tree.getNodeByHandle(newNode.dynamicFolder.handle, this.foldersNode);
                    if (selNode) {
                        this.tree.selectNode(selNode);
                    }
                }
            }, error => {
                this.foldersNode.isLoading = false;
            });
        }
    }

    ok() {
        let request = null;
        let endpoint: Endpoint;
        let destHandle = '';
        let destName = '';
        if (this.selectedNode.name === 'Project Data') {
            destHandle = this.commonService.projectData.handle;
            destName = 'Project Data';
        } else {
            destHandle = this.selectedNode.data.view ? this.selectedNode.data.view.handle : this.selectedNode.data.handle;
            destName = this.selectedNode.data.name;
        }
        if (this.operationType === SaveToRemoveFromOperationType.add) {
            this.isBusyMessage = 'Saving Documents..';
            if (this.targetType === ModalTargetType.view) {
                let srcViewHandle = this.targets[0].handle;
                if (this.targets[0] instanceof Corpus) {
                    srcViewHandle = this.targets[0].corpusViewHandle;
                } else if (this.targets[0] instanceof SavedSearch) {
                    srcViewHandle = this.targets[0].view.handle;
                }
                endpoint = Endpoint.viewManager_addFromView;
                let name = this.targets[0].name ? this.targets[0].name : 'search results';

                if (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn) {
                    srcViewHandle = this.commonService.matrixGridsSelection?.viewHandle;
                    name = this.commonService.matrixGridsSelection?.viewName;
                }
                request = {
                    filterSrcDuplicates: false,
                    includeFamilyMembers: this.includeFamilies ? this.includeFamilies : false,
                    destViewHandle: destHandle,
                    srcViewHandle: this.targets[0] instanceof DynamicFolder ? this.targets[0].view.handle : srcViewHandle,
                    taskDescription: 'Adding documents from ' + name + ' to ' + destName
                };
            } else if (this.targetType === ModalTargetType.document) {
                endpoint = Endpoint.viewManager_addFromDocuments;
                request = {
                    includeFamilyMembers: this.includeFamilies ? this.includeFamilies : false,
                    destViewHandle: destHandle,
                    srcDocumentHandleList: this.targets,
                    taskDescription: 'Adding documents to ' + destName
                };
            } else if (this.targetType === ModalTargetType.emailThread) {
                endpoint = Endpoint.viewManager_addFromEmailThread;
                request = {
                    destViewHandle: destHandle,
                    emailThreadHandleList: this.targets,
                    taskDescription: 'Adding email threads to ' + destName
                };
            } else if (this.targetType === ModalTargetType.report) {
                const srcViewHandle = this.targets[0].viewHandle;
                endpoint = Endpoint.viewManager_addFromView;
                request = {
                    filterSrcDuplicates: false,
                    includeFamilyMembers: this.includeFamilies ? this.includeFamilies : false,
                    destViewHandle: destHandle,
                    srcViewHandle: srcViewHandle,
                    taskDescription: 'Adding documents from View to ' + destName
                };
            } else if (this.targetType === ModalTargetType.selection) {
                this.closeModal(true, this.selectedNode);
            }
        } else if (this.operationType === SaveToRemoveFromOperationType.remove) {
            this.isBusyMessage = 'Removing Documents..';
            if (this.targetType === ModalTargetType.view) {
                let srcViewHandle = this.targets[0].handle;
                if (this.targets[0] instanceof Corpus) {
                    srcViewHandle = this.targets[0].corpusViewHandle;
                } else if (this.targets[0] instanceof SavedSearch) {
                    srcViewHandle = this.targets[0].view.handle;
                }
                if (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn) {
                    srcViewHandle = this.commonService.matrixGridsSelection?.viewHandle;
                }
                endpoint = Endpoint.viewManager_removeFromView;
                request = {
                    targetViewHandle: destHandle,
                    srcViewHandle: this.targets[0] instanceof DynamicFolder ? this.targets[0].view.handle : srcViewHandle,
                    taskDescription: 'Removing documents from ' + destName,
                    includeFamilies: this.includeFamilies
                };
            } else if (this.targetType === ModalTargetType.document) {
                endpoint = Endpoint.viewManager_removeFromDocuments;
                request = {
                    documentsHandleList: this.targets,
                    targetViewHandle: destHandle,
                    taskDescription: 'Removing documents from ' + destName,
                    includeFamilies: this.includeFamilies
                };
            } else if (this.targetType === ModalTargetType.emailThread) {
                endpoint = Endpoint.viewManager_removeFromEmailThreads;
                request = {
                    targetViewHandle: destHandle,
                    emailThreadHandleList: this.targets,
                    taskDescription: 'Removing email threads from ' + destName,
                    includeFamilies: this.includeFamilies
                };
            } else if (this.targetType === ModalTargetType.report) {
                const srcViewHandle = this.targets[0].viewHandle;
                endpoint = Endpoint.viewManager_removeFromView;
                request = {
                    targetViewHandle: destHandle,
                    srcViewHandle: srcViewHandle,
                    taskDescription: 'Removing documents from View',
                    includeFamilies: this.includeFamilies
                };
            }
        }
        if (endpoint && request) {
            const updateStatusType = this.commonService.entityTypeToUpdateStatusType(this.selectedNode.entityType);
            this.isBusy = true;
            this.restService.post(endpoint, request, true, updateStatusType ?
                [MessageType.entityChanged, updateStatusType] : true);
            this.isBusy = false;
            this.closeModal(true);
        }
    }

    isPrimaryButtonEnabled() {
        return this.targetType === ModalTargetType.selection ? this.selectedNode : this.selectedNode && this.targets && this.targets.length > 0;
    }

    newCustodian() {
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: new Custodian({ priorityEnabled: false }) }).then((value) => {
            if (value.modalResult) {
                this.getData(value.modalData);
            }
        });
    }

    newFolder() {
        this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.FOLDER } }).then((value) => {
            if (value.modalResult) {
                this.getData(value.modalData);
            }
        });
    }

    newMediaID() {
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: new MediaID() }).then((value) => {
            if (value.modalResult) {
                this.getData(value.modalData);
            }
        });
    }

    newBatch() {
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: new Batch() }).then((value) => {
            if (value.modalResult) {
                this.getData(value.modalData);
            }
        });
    }

    filterData(str: string): void {
        if (str) {
            this.custodiansNode.filterBy = node => node.name.toLowerCase().includes(str) || node.isButton;
            this.foldersNode.filterBy = node => node.name.toLowerCase().includes(str) || node.isButton;
            this.mediaidsNode.filterBy = node => node.name.toLowerCase().includes(str) || node.isButton;
            this.batchesNode.filterBy = node => node.name.toLowerCase().includes(str) || node.isButton;
        } else {
            this.custodiansNode.filterBy = null;
            this.foldersNode.filterBy = null;
            this.mediaidsNode.filterBy = null;
            this.batchesNode.filterBy = null;
        }
        this.tree.treeDataUpdated();
        this.tree.selectedNode = null;
        this.selectedNode = null;
    }
}
